import { Fragment } from 'react';
import { FileDown, X } from 'lucide-react';
import {
  buildFinancialSummaryFilename,
  downloadFinancialSummaryXlsx,
  type FinancialSummaryData,
} from '../../utils/sessionFinancialSummaryExport';

type CourseFinancialSummaryModalProps = {
  data: FinancialSummaryData;
  canDownload: boolean;
  onClose: () => void;
};

function money(value: number): string {
  return `€${value.toFixed(2)}`;
}

export function CourseFinancialSummaryModal({
  data,
  canDownload,
  onClose,
}: CourseFinancialSummaryModalProps) {
  const { session, datesLabel, locationLabel, generatedAt, overview, collectedProfit, profitTotal, participantGroups, paymentRows } =
    data;
  const filename = buildFinancialSummaryFilename(session);

  return (
    <div className="fixed inset-0 z-[79] flex items-center justify-center bg-black/60 p-3">
      <div className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-slate-700">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-slate-50">Course Financial Summary</p>
            <p className="truncate text-xs text-gray-500 dark:text-slate-400">
              {session.courseName}
              {session.language ? ` · ${session.language}` : ''} · {datesLabel}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canDownload ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                onClick={() => downloadFinancialSummaryXlsx(data, filename)}
              >
                <FileDown className="h-3.5 w-3.5" />
                Download
              </button>
            ) : (
              <span className="text-[10px] text-gray-500 dark:text-slate-400">Preview only</span>
            )}
            <button
              type="button"
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-50 p-4 dark:bg-slate-950">
          <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">Summary</h3>
            <p className="mt-1 text-[11px] text-gray-500">
              {locationLabel} · Generated {generatedAt}
            </p>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-xs text-gray-500">Total fees</dt>
                <dd className="font-semibold">{money(overview.totalFee)}</dd>
              </div>
              <div>
                <dt className="text-xs text-emerald-600">Collected</dt>
                <dd className="font-semibold text-emerald-700">{money(overview.totalCollected)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Prepaid</dt>
                <dd className="font-semibold">{money(overview.totalPrepaid)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">On-site</dt>
                <dd className="font-semibold">{money(overview.totalDirectCollected)}</dd>
              </div>
              <div>
                <dt className="text-xs text-red-600">Outstanding</dt>
                <dd className="font-semibold text-red-700">{money(overview.totalOutstanding)}</dd>
              </div>
              <div>
                <dt className="text-xs text-violet-600">Overpayment</dt>
                <dd className="font-semibold text-violet-700">{money(overview.totalOverpayment)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Collected profit</dt>
                <dd className="font-semibold">{money(collectedProfit)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Profit total</dt>
                <dd className="font-semibold">{money(profitTotal)}</dd>
              </div>
            </dl>
          </section>

          <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">Participants</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-[10px] uppercase tracking-wide text-gray-500">
                    <th className="px-2 py-1.5">Company</th>
                    <th className="px-2 py-1.5">Participant</th>
                    <th className="px-2 py-1.5">Status</th>
                    <th className="px-2 py-1.5 text-right">Fee</th>
                    <th className="px-2 py-1.5 text-right">Paid</th>
                    <th className="px-2 py-1.5 text-right">Balance</th>
                    <th className="px-2 py-1.5">Payment</th>
                    <th className="px-2 py-1.5">Invoice #</th>
                    <th className="px-2 py-1.5">Inv. paid</th>
                  </tr>
                </thead>
                <tbody>
                  {participantGroups.map((group) => (
                    <Fragment key={group.label}>
                      <tr className="bg-gray-50 dark:bg-slate-800/60">
                        <td colSpan={9} className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-600">
                          {group.label} ({group.rows.length})
                        </td>
                      </tr>
                      {group.rows.map((row, idx) => (
                        <tr key={`${group.label}-${row.attendeeName}-${idx}`} className="border-b border-gray-100 dark:border-slate-800">
                          <td className="px-2 py-1.5">{row.companyName}</td>
                          <td className="px-2 py-1.5">{row.attendeeName}</td>
                          <td className="px-2 py-1.5">{row.status}</td>
                          <td className="px-2 py-1.5 text-right">{money(row.feeEuro)}</td>
                          <td className="px-2 py-1.5 text-right">{money(row.paidEuro)}</td>
                          <td className="px-2 py-1.5 text-right">{money(row.balanceEuro)}</td>
                          <td className="px-2 py-1.5">{row.paymentStatus}</td>
                          <td className="px-2 py-1.5">{row.invoiceNumber}</td>
                          <td className="px-2 py-1.5">{row.invoicePaid}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">
              Payments ({paymentRows.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-[10px] uppercase tracking-wide text-gray-500">
                    <th className="px-2 py-1.5">Participant</th>
                    <th className="px-2 py-1.5">Company</th>
                    <th className="px-2 py-1.5">Status</th>
                    <th className="px-2 py-1.5">Date</th>
                    <th className="px-2 py-1.5">Method</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                    <th className="px-2 py-1.5">Step</th>
                    <th className="px-2 py-1.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-100 dark:border-slate-800">
                      <td className="px-2 py-1.5">{row.participant}</td>
                      <td className="px-2 py-1.5">{row.company}</td>
                      <td className="px-2 py-1.5">{row.status}</td>
                      <td className="px-2 py-1.5">{row.date}</td>
                      <td className="px-2 py-1.5">{row.method}</td>
                      <td className="px-2 py-1.5 text-right">{money(row.amount)}</td>
                      <td className="px-2 py-1.5">{row.stepType}</td>
                      <td className="px-2 py-1.5">{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
